import { useEffect, useRef, useCallback, useState } from "react";

const VITE_API = import.meta.env.VITE_API_URL || "";
const WS_BASE =
  import.meta.env.VITE_WS_URL ||
  (typeof window !== "undefined"
    ? VITE_API.startsWith("http")
      ? VITE_API.replace(/^http/, "ws")
      : (window.location.protocol === "https:" ? "wss" : "ws") +
        "://" +
        window.location.host +
        (VITE_API || "/api/v1")
    : "ws://localhost:8000/api/v1");

export type WsStatus = "connecting" | "connected" | "disconnected";

export function useWebSocket(
  path: string | null,
  token: string | null,
  onEvent: (event: any) => void,
): { wsStatus: WsStatus } {
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEventRef = useRef(onEvent);
  const [wsStatus, setWsStatus] = useState<WsStatus>("connecting");
  onEventRef.current = onEvent;

  const connect = useCallback(() => {
    if (!path || !token) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setWsStatus("connecting");
    const url = `${WS_BASE}${path}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setWsStatus("connected");

    ws.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data);
        onEventRef.current(event);
      } catch {
        /* ignore malformed */
      }
    };

    ws.onclose = () => {
      setWsStatus("disconnected");
      // Auto-reconnect after 2 seconds
      retryRef.current = setTimeout(connect, 2000);
    };

    ws.onerror = () => ws.close();
  }, [path, token]);

  useEffect(() => {
    connect();
    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect on intentional close
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, path]);

  return { wsStatus };
}
